/**
 * 场景概览面板(嵌入式)
 *
 * 嵌入「场景管理」右侧主区域,作为「未选中场景集节点」时的默认视图。
 *
 * 布局(v3):
 * - 左侧一级(sim_type):场景总数 / PNC 场景 / 感知场景          (3 卡)
 * - 中间二级(source):来源总数 / Caseflow / Jira / 数据中台     (4 卡)
 * - 右侧: flex:1 趋势折线,一条线,内容 = 一级 ∩ 二级
 *
 * 交互:
 * - 一级各选 1(默认「场景总数」= 不按 sim_type 过滤)
 * - 二级各选 1(默认「来源总数」= 不按 source 过滤,累加全部)
 * - 图数据 = trend({ sources:[activeSource]|undefined,
 *                    simType: activeSim || undefined,
 *                    mode: cumulative(默认)/delta })
 * - 卡片显示的数字 = summary.byXxx[activeKey] 或 summary.total
 * - 「累计 / 新增」右上角切换,默认累计(最近总量变化)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Radio,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  AimOutlined,
  AppstoreOutlined,
  ClusterOutlined,
  CompassOutlined,
  DatabaseOutlined,
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption, LineSeriesOption } from 'echarts';
import dayjs, { type Dayjs } from 'dayjs';

import {
  getSceneSourceSummary,
  getSceneTrend,
  type SceneSourceSummary,
  type SceneTrendData,
  type SceneTrendGranularity,
} from '@/apis/simulation';

const { Title } = Typography;
const { RangePicker } = DatePicker;

// ─── 一级(sim_type) ────────────────────────────────────────────────
// 「场景总数」的 simKey = null 表示不按 sim_type 过滤(累加全部)。
// 其它两值对应 scene_category.sim_type 白名单。
type SimKey = null | 'planning' | 'perception';
type SimOption = {
  key: SimKey;
  /** summary.bySimType 里累加的具体 key(总数走 total,故此字段可空) */
  summaryKey?: 'planning' | 'perception';
  label: string;
  icon: React.ReactNode;
  color: string;
};

const SIM_OPTIONS: SimOption[] = [
  { key: null,          label: '场景总数', icon: <AppstoreOutlined />, color: '#5e6ad2' },
  { key: 'planning',    summaryKey: 'planning',   label: 'PNC 场景',  icon: <AimOutlined />,      color: '#3f6ad8' },
  { key: 'perception',  summaryKey: 'perception', label: '感知场景',   icon: <EyeOutlined />,      color: '#10b981' },
];

// ─── 二级(source) ─────────────────────────────────────────────────
// 与 SceneSourceEnum 保持一致的 key,展示名走 SceneSourceEnum.display。
// 「来源总数」sourceKey=null → 不按 source 过滤,图纵坐标 = 该桶所有 source 求和。
type SourceKey = null | 'case_flow' | 'jira' | 'data_platform';
type SourceOption = {
  key: SourceKey;
  label: string;
  icon: React.ReactNode;
  color: string;
};

const SOURCE_OPTIONS: SourceOption[] = [
  { key: null,            label: '来源总数', icon: <AppstoreOutlined />, color: '#5e6ad2' },
  { key: 'case_flow',     label: 'Caseflow', icon: <ClusterOutlined />,  color: '#f6bd16' },
  { key: 'jira',          label: 'Jira',     icon: <CompassOutlined />,  color: '#5b8ff9' },
  { key: 'data_platform', label: '数据中台', icon: <DatabaseOutlined />, color: '#8c54ff' },
];

const GRANULARITY_OPTIONS: { value: SceneTrendGranularity; label: string }[] = [
  { value: 'day', label: '日' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

/** dayjs → 后端 ISO(不带 Z;与全站 createTimeStart 一致)。 */
const fmtIso = (d: Dayjs): string => d.format('YYYY-MM-DDTHH:mm:ss');

/** 按粒度计算合理默认窗口(与后端 `_default_start_date` 保持一致)。 */
const calcDefaultRange = (
  granularity: SceneTrendGranularity,
): [Dayjs, Dayjs] => {
  const end = dayjs();
  if (granularity === 'month') {
    return [end.subtract(11, 'month').startOf('month'), end];
  }
  if (granularity === 'year') {
    return [end.subtract(4, 'year').startOf('year'), end];
  }
  return [end.subtract(6, 'day').startOf('day'), end];
};

interface SceneOverviewPanelProps {
  /**
   * 限定统计范围到某场景树节点(目录/叶子)下的场景;不传=全库。
   * 目录递归含所有后代叶子(与后端 helper 语义一致)。
   */
  treeNodeId?: number | null;
}

const SceneOverviewPanel: React.FC<SceneOverviewPanelProps> = ({ treeNodeId }) => {
  // 一级 / 二级各选 1
  const [simKey, setSimKey] = useState<SimKey>(null);
  const [sourceKey, setSourceKey] = useState<SourceKey>(null);
  // 折线口径:默认 cumulative(最近总量变化)
  const [trendMode, setTrendMode] = useState<'delta' | 'cumulative'>('cumulative');

  // —— summary ——
  const [summary, setSummary] = useState<SceneSourceSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await getSceneSourceSummary({
        treeNodeId: treeNodeId ?? undefined,
      });
      setSummary(data);
    } catch (e) {
      message.error('加载场景统计失败');
    } finally {
      setSummaryLoading(false);
    }
  }, [treeNodeId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // —— trend ——
  const [granularity, setGranularity] = useState<SceneTrendGranularity>('day');
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => calcDefaultRange('day'));
  const [rangeDirty, setRangeDirty] = useState(false);
  const [trend, setTrend] = useState<SceneTrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const onGranularityChange = (g: SceneTrendGranularity) => {
    setGranularity(g);
    if (!rangeDirty) {
      setRange(calcDefaultRange(g));
    }
  };

  const onRangeChange = (vals: any) => {
    if (vals && vals[0] && vals[1]) {
      setRange([vals[0], vals[1]]);
      setRangeDirty(true);
    }
  };

  const onResetRange = () => {
    setRange(calcDefaultRange(granularity));
    setRangeDirty(false);
  };

  const loadTrend = useCallback(async () => {
    setTrendLoading(true);
    try {
      const data = await getSceneTrend({
        startDate: fmtIso(range[0]),
        endDate: fmtIso(range[1]),
        granularity,
        // sourceKey=null → 不传 sources,后端返回全部 enum
        sources: sourceKey ? [sourceKey] : undefined,
        simType: simKey ?? undefined,
        mode: trendMode,
        treeNodeId: treeNodeId ?? undefined,
      });
      setTrend(data);
    } catch (e) {
      message.error('加载场景趋势失败');
    } finally {
      setTrendLoading(false);
    }
  }, [granularity, range, sourceKey, simKey, trendMode, treeNodeId]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  const chartOption: EChartsOption = useMemo(() => {
    const buckets = trend?.buckets ?? [];
    const xData = buckets.map((b) => b.bucket);
    const simOpt = SIM_OPTIONS.find((o) => o.key === simKey)!;
    const sourceOpt = SOURCE_OPTIONS.find((o) => o.key === sourceKey)!;
    // 图上永远只画一条线:一级 ∩ 二级(sourceKey=null → 各 source 求和)
    const lineName = (() => {
      const parts: string[] = [];
      if (simKey !== null) parts.push(simOpt.label);
      if (sourceKey !== null) parts.push(sourceOpt.label);
      const scope = parts.length ? parts.join(' / ') : '全部场景';
      return `${scope} · ${trendMode === 'cumulative' ? '累计' : '新增'}`;
    })();
    const lineColor = sourceKey !== null ? sourceOpt.color : simOpt.color;
    // sourceKey=null 时把该桶所有 source 求和
    const seriesData = buckets.map((b) => {
      if (sourceKey !== null) return b.counts[sourceKey] ?? 0;
      return Object.values(b.counts).reduce((a, v) => a + (v ?? 0), 0);
    });
    const series: LineSeriesOption[] = [
      {
        name: lineName,
        type: 'line',
        smooth: true,
        showSymbol: buckets.length <= 30,
        symbol: 'circle',
        symbolSize: 6,
        emphasis: { focus: 'series' },
        lineStyle: { width: 2, color: lineColor },
        itemStyle: { color: lineColor },
        areaStyle: { color: lineColor, opacity: 0.1 },
        data: seriesData,
      },
    ];
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'line' } },
      legend: { data: [lineName], right: 0, top: 0 },
      grid: { left: 40, right: 24, top: 36, bottom: 32, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: xData,
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLabel: { formatter: (v: number) => `${v}` },
      },
      series,
    };
  }, [trend, simKey, sourceKey, trendMode]);

  const trendRangeHint = trend
    ? `统计窗口:${dayjs(trend.startDate).format('YYYY-MM-DD')} ~ ${dayjs(
        trend.endDate,
      ).format('YYYY-MM-DD')}`
    : '';

  // 空数据兜底(sourceKey=null 时看所有 source 求和)
  const trendIsEmpty = !!trend && trend.buckets.every((b) => {
    if (sourceKey !== null) return (b.counts[sourceKey] ?? 0) === 0;
    return Object.values(b.counts).every((v) => (v ?? 0) === 0);
  });

  // 卡片样式(compact 版)。选中态用 accent 边框 + 阴影。
  const buildCardStyle = (accent: string, selected: boolean): React.CSSProperties => ({
    padding: '10px 14px',
    borderRadius: 8,
    border: selected ? `1px solid ${accent}` : '1px solid #f0f0f0',
    background: '#fff',
    cursor: 'pointer',
    transition: 'border-color .15s',
    boxShadow: selected ? `0 2px 8px ${accent}22` : 'none',
    userSelect: 'none',
  });
  const cardTitleStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 12, color: '#6b6f76', marginBottom: 4,
  };
  const cardValueStyle: React.CSSProperties = {
    fontSize: 22, fontWeight: 600, lineHeight: 1.1, color: '#1d1d1f',
    fontVariantNumeric: 'tabular-nums',
  };
  const unitStyle: React.CSSProperties = {
    fontSize: 12, color: '#8c8c8c', fontWeight: 400, marginLeft: 4,
  };

  const simCount = (opt: SimOption): number => {
    if (opt.key === null) return summary?.total ?? 0;
    return summary?.bySimType?.[opt.summaryKey!] ?? 0;
  };
  const sourceCount = (opt: SourceOption): number => {
    if (opt.key === null) return summary?.total ?? 0;
    return summary?.bySource?.[opt.key] ?? 0;
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
        {/* 左列:一级 sim_type + 「已禁用」只读展示卡 */}
        <Spin spinning={summaryLoading}>
          <div style={{ width: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SIM_OPTIONS.map((opt) => {
              const selected = simKey === opt.key;
              return (
                <div
                  key={String(opt.key)}
                  style={buildCardStyle(opt.color, selected)}
                  onClick={() => setSimKey(opt.key)}
                >
                  <div style={cardTitleStyle}>
                    <span style={{ color: opt.color }}>{opt.icon}</span>
                    {opt.label}
                  </div>
                  <div style={cardValueStyle}>
                    {simCount(opt)}<span style={unitStyle}>个</span>
                  </div>
                </div>
              );
            })}
            {/* 已禁用卡:只展示不参与筛选,与 sim_type 三卡分开一档;色标用中性灰。
                summary.total 已排除 is_deleted=1,这里的数字与 total 相加即列表总条数,
                消除"面板 2889 vs 列表 3000"的口径歧义。 */}
            <div
              style={{
                ...buildCardStyle('#8c8c8c', false),
                cursor: 'default',
                opacity: 0.85,
              }}
              title="已被软删的场景数(is_deleted=1);不进入其它维度统计"
            >
              <div style={cardTitleStyle}>
                <span style={{ color: '#8c8c8c' }}>⊘</span>
                已禁用
              </div>
              <div style={{ ...cardValueStyle, color: '#8c8c8c' }}>
                {summary?.disabledTotal ?? 0}<span style={unitStyle}>个</span>
              </div>
            </div>
          </div>
        </Spin>

        {/* 中列:二级 source */}
        <Spin spinning={summaryLoading}>
          <div style={{ width: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SOURCE_OPTIONS.map((opt) => {
              const selected = sourceKey === opt.key;
              return (
                <div
                  key={String(opt.key)}
                  style={buildCardStyle(opt.color, selected)}
                  onClick={() => setSourceKey(opt.key)}
                >
                  <div style={cardTitleStyle}>
                    <span style={{ color: opt.color }}>{opt.icon}</span>
                    {opt.label}
                  </div>
                  <div style={cardValueStyle}>
                    {sourceCount(opt)}<span style={unitStyle}>个</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Spin>

        {/* 右列:趋势图 */}
        <Card
          style={{ flex: 1, minWidth: 0 }}
          styles={{ body: { padding: '10px 14px' } }}
          size="small"
          title={
            <Space size={8}>
              <Title level={5} style={{ margin: 0, fontSize: 13 }}>
                场景变化趋势图
              </Title>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {trendRangeHint}
              </Typography.Text>
            </Space>
          }
          extra={
            <Space size={4}>
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={trendMode}
                onChange={(e) => setTrendMode(e.target.value)}
                options={[
                  { value: 'cumulative', label: '累计' },
                  { value: 'delta', label: '新增' },
                ]}
              />
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={granularity}
                onChange={(e) => onGranularityChange(e.target.value)}
                options={GRANULARITY_OPTIONS}
              />
              <RangePicker
                size="small"
                picker={granularity === 'day' ? 'date' : granularity}
                value={range}
                onChange={onRangeChange}
                allowClear={false}
              />
              {rangeDirty && (
                <Button size="small" onClick={onResetRange}>
                  重置
                </Button>
              )}
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadSummary();
                  loadTrend();
                }}
              />
            </Space>
          }
        >
          <Spin spinning={trendLoading}>
            {trendIsEmpty && !trendLoading && (
              <div style={{ fontSize: 12, color: '#6b6f76', marginBottom: 4 }}>
                {trendMode === 'cumulative'
                  ? '当前窗口该范围累计为 0。可切「月」/「年」看长期变化。'
                  : '当前窗口无新增,可切「月」/「年」看历史分布。'}
              </div>
            )}
            <div style={{ height: 200 }}>
              <ReactECharts
                option={chartOption}
                notMerge
                lazyUpdate
                style={{ height: '100%', width: '100%' }}
              />
            </div>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default SceneOverviewPanel;
